import styles from "./accounts.module.css";
import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useFetchBillingDetailsQuery } from "../../Apis/billingApi";
import { useFetchCompanyInfosQuery } from "../../Apis/companyApi";
import { plansSelector } from "../../Reducers/plansReducer";
import { enterpriseDetailsSelector } from "../../Reducers/enterpriseDetailsReducer";
import { allCompanyReviewSummarySelector } from "../../Reducers/allCompanyReviewSummaryReducer";
import { getTotalDueAndSavingForCurrentMonth } from "../../Util/dataHelper";
import InfoIcon from "../Icons/info-icon";
import AccountsTable from "./AccountsTable";
import AddAccountModal from "./AddAccountModal";
import useListProcessing from "../../Hooks/useListProcessing";
import BusinessProfilePageWrapper from "../BusinessProfile/BusinessProfilePageWrapper";
import { activeCompanySelector } from "../../Reducers/activeCompanyReducer";
import { fullScreenLayoutSelector } from "../../Reducers/selectedComponentReducer";
import { ownPermissionsSelector } from "../../Reducers/ownRoleReducer";

export const ACCOUNTS_COMPONENT = "ACCOUNTS_COMPONENT";

const AccountsHeader = ({ onAddAccount }) => {
    return (
        <div className={styles.accountsHeader}>
            <div className={styles.accountsTitle}>Accounts</div>
            <button
                className={styles.addAccountBtn}
                onClick={onAddAccount}
                type="button"
            >
                <span className={styles.addAccountIcon}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="6" width="2" height="14" rx="1" fill="white"/>
                        <rect y="8" width="2" height="14" rx="1" transform="rotate(-90 0 8)" fill="white"/>
                    </svg>
                </span>
                <span className={styles.addAccountBtnText}>Add new account</span>
            </button>
        </div>
    );
};

const InfoCards = () => {
    const enterpriseInfo = useSelector(enterpriseDetailsSelector);
    const { data: partnerAccountsObj } = useFetchCompanyInfosQuery();
    const ownPermissions = useSelector(ownPermissionsSelector);
    const { data: billingDataObj } = useFetchBillingDetailsQuery(undefined, {
        skip: !ownPermissions?.canReadBilling(),
    });
    const plans = useSelector(plansSelector);

    let infos = [
        {
            id: "activeAccounts",
            title: "Active Accounts",
            value: 0,
        },
        {
            id: "paidAccounts",
            title: "Paid Accounts",
            value: 0,
        },
        {
            id: "currentMonthDue",
            title: "Current Month Due",
            value: "$0",
        },
    ]

    if (!enterpriseInfo || !partnerAccountsObj) return null;

    infos[0].value = partnerAccountsObj.getTotalCountOfActiveAccounts();
    infos[1].value = partnerAccountsObj.getTotalCountOfPaidAccounts(plans);

    if (billingDataObj) {
        const { afterDiscountAmount } = getTotalDueAndSavingForCurrentMonth(
            billingDataObj.billingFormattedObj
        );
        infos[2].value = "$" + afterDiscountAmount;
    }

    if (!ownPermissions.canReadBilling()) {
        infos = infos.filter(info => info.id !== "currentMonthDue");
    }

    return (
        <div className={styles.infoCardsContainer}>
            {infos.map((info, idx) => (
                <div key={info.id} className={styles.infoCard}>
                    <div className={styles.infoCardHeader}>
                        <div className={styles.infoCardTitle}>{info.title}</div>
                        <div className={styles.infoCardIcon}>
                            <InfoIcon/>
                        </div>
                    </div>
                    <div className={styles.infoCardBody}>
                        <div className={styles.infoCardValue}>{info.value}</div>
                        <div></div>
                        {/* <div className={`${styles.infoCardBadge} ${idx === 1 ? styles.infoCardBadgeRed : styles.infoCardBadgeGreen}`}>
                            <div className={styles.infoCardIncrement}></div>
                            <div className={`${styles.infoCardBadgeText} ${idx === 1 ? styles.infoCardBadgeTextRed : styles.infoCardBadgeTextGreen}`}>&nbsp;</div>
                        </div> */}
                    </div>
                </div>
            ))}
        </div>
    );
};

const processConfigs = {
    filter: {
        SEARCH: ({account}, value) => {
            if (!value) return true;
            const searchTerm = value.toLowerCase();
            return account.getCompanyName().toLowerCase().includes(searchTerm) || 
                   account.getEmail().toLowerCase().includes(searchTerm) || 
                   account.getCountry().toLowerCase().includes(searchTerm) ||
                   account.getAdminName().toLowerCase().includes(searchTerm);
        },
        ORDER_RATING: ({orderRating}, values) => {
            if (!values || values.length === 0) return true;
            if (orderRating === "N/A") orderRating = 0;
            
            const rating = parseFloat(orderRating);
            return values.some(range => {
                const [min, max] = range;
                return rating >= min && rating <= max;
            });
        },
        DRIVER_RATING: ({driverRating}, values) => {
            if (!values || values.length === 0) return true;
            if (driverRating === "N/A") driverRating = 0;
            
            const rating = parseFloat(driverRating);
            return values.some(range => {
                const [min, max] = range;
                return rating >= min && rating <= max;
            });
        },
        PLAN: ({planName}, values) => {
            if (!values || values.length === 0) return true;
            if (planName === "-") return true;
            return values.includes(planName);
        }
    },
    pagination: {
        pageSize: 10,
    }
}   

const AccountsWrapper = () => {
    const { isLoading, data: partnerAccountObj } = useFetchCompanyInfosQuery();
    const allCompanyReviewSummary = useSelector(allCompanyReviewSummarySelector);
    const plans = useSelector(plansSelector);
    const [modalOpen, setModalOpen] = useState(false);
    const activeCompanyId = useSelector(activeCompanySelector);
    const fullScreen = useSelector(fullScreenLayoutSelector);

    const accountsData = useMemo(() => {
        if (!partnerAccountObj) return [];
        return partnerAccountObj.getAccountsArray().map(account => ({
            account: account,
            orderRating: allCompanyReviewSummary?.getCompanyOrderRating(account.companyId) ?? "N/A",
            driverRating: allCompanyReviewSummary?.getCompanyDriverRating(account.companyId) ?? "N/A",
            planName: plans?.getDisplayName(account.currentPlan) ?? "-",
        }));
    }, [partnerAccountObj, allCompanyReviewSummary, plans]);

    const { processedData, totalPages, currentPageIndex, setCurrentPageIndex, setFilter, getFilter } = useListProcessing(accountsData, processConfigs);

    if (isLoading || !partnerAccountObj) {
        return null;
    }

    if (activeCompanyId) {
        return <BusinessProfilePageWrapper />
    }

    if (fullScreen) {
        return <></>
    }

    return (
        <div className={styles.accountsContainer}>
            <div className={styles.accountsContent}>
                <AccountsHeader onAddAccount={() => setModalOpen(true)} />
                <InfoCards/>
                <AccountsTable 
                    processedData={processedData} 
                    pageSize={processConfigs.pagination.pageSize} 
                    totalPages={totalPages} 
                    currentPageIndex={currentPageIndex} 
                    setCurrentPageIndex={setCurrentPageIndex}
                    getFilter={getFilter}
                    setFilter={setFilter}
                />
                <AddAccountModal open={modalOpen} onClose={() => setModalOpen(false)} />
            </div>
        </div>
    )
}

export default AccountsWrapper;
